// src/generators/dzial1_liczby_i_dzialania/temat1_dzialania/podtemat3_zbiory_types/VennSetPaintTask.js
import React from "react";
import { InlineMath } from "react-katex";
import Venn3Input from "../../../../components/inputs/Venn3Input";

const MASKS = [1, 2, 3, 4, 5, 6, 7];
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

function compileExpr(expr) {
  const fn = (node) => {
    if (node.var) {
      const bit = node.var === "A" ? 1 : node.var === "B" ? 2 : 4;
      return (m) => (m & bit) !== 0;
    }
    const L = fn(node.left);
    const R = fn(node.right);
    if (node.op === "union") return (m) => L(m) || R(m);
    if (node.op === "inter") return (m) => L(m) && R(m);
    if (node.op === "diff") return (m) => L(m) && !R(m);
    throw new Error("Unknown op");
  };
  return fn(expr);
}

function exprToLatex(node) {
  if (node.var) return node.var;
  const op = node.op === "union" ? "\\cup" : node.op === "inter" ? "\\cap" : "\\setminus";
  const L = node.left.var ? node.left.var : `\\left(${exprToLatex(node.left)}\\right)`;
  const R = node.right.var ? node.right.var : `\\left(${exprToLatex(node.right)}\\right)`;
  return `${L} ${op} ${R}`;
}

const TEMPLATES = [
  { t: (a, b) => ({ op: "union", left: { var: a }, right: { var: b } }) },
  { t: (a, b) => ({ op: "inter", left: { var: a }, right: { var: b } }) },
  { t: (a, b) => ({ op: "diff", left: { var: a }, right: { var: b } }) },
  { t: (a, b, c) => ({ op: "diff", left: { op: "union", left: { var: a }, right: { var: b } }, right: { var: c } }) },
  { t: (a, b, c) => ({ op: "union", left: { op: "inter", left: { var: a }, right: { var: b } }, right: { var: c } }) },
  { t: (a, b, c) => ({ op: "inter", left: { op: "union", left: { var: a }, right: { var: b } }, right: { var: c } }) },
  { t: (a, b, c) => ({ op: "union", left: { op: "diff", left: { var: a }, right: { var: b } }, right: { var: c } }) },
  { t: (a, b, c) => ({ op: "inter", left: { op: "diff", left: { var: a }, right: { var: b } }, right: { var: c } }) },
  { t: (a, b, c) => ({ op: "diff", left: { op: "inter", left: { var: a }, right: { var: b } }, right: { var: c } }) },
];

function permABC() {
  const v = ["A", "B", "C"];
  for (let i = v.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [v[i], v[j]] = [v[j], v[i]];
  }
  return v;
}

function ExerciseVennInput() {
  const [value, setValue] = React.useState([]);
  return (
    <div className="mt-2 space-y-2">
      <p className="text-sm opacity-80">
        Kliknij wybrane pola diagramu. Ponowne kliknięcie odznacza pole.
      </p>
      <Venn3Input
        value={value}
        onChange={setValue}
        size={360}
        ariaLabel="Interaktywny diagram Venna do ćwiczenia"
      />
    </div>
  );
}

export function generateTask() {
  const [a, b, c] = permABC();
  const tmpl = pick(TEMPLATES);
  const expr = tmpl.t.length === 2 ? tmpl.t(a, b) : tmpl.t(a, b, c);

  const pred = compileExpr(expr);
  const correct = MASKS.filter(pred).sort((x, y) => x - y);

  return { type: "VennSetPaintTask", data: { expr, correct } };
}

export function renderTask({ data }) {
  const { expr } = data;
  return (
    <div className="space-y-2">
      <p>
        <strong>Zaznacz na diagramie Venna odpowiedni zbiór.</strong>
      </p>
      <p>
        Wyrażenie: <InlineMath math={exprToLatex(expr)} />
      </p>
    </div>
  );
}

export function renderExerciseTask(task) {
  return (
    <div>
      {renderTask(task)}
      <ExerciseVennInput />
    </div>
  );
}

export function renderInput(_, userAnswer, onChange, disabled) {
  const val = Array.isArray(userAnswer) ? userAnswer : [];
  return (
    <div className="mt-2 space-y-2">
      <p className="text-sm opacity-80">
        Kliknij w wybrane pole diagramu, aby je zamalować. Ponowne kliknięcie odznacza pole.
      </p>
      <Venn3Input
        value={val}
        onChange={onChange}
        disabled={disabled}
        size={360}
        ariaLabel="Interaktywny diagram Venna (A, B, C)"
      />
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  const got = Array.isArray(userAnswer) ? [...userAnswer].sort((a, b) => a - b) : [];
  const exp = [...data.correct].sort((a, b) => a - b);
  const ok = got.length === exp.length && got.every((x, i) => x === exp[i]);
  return { isCorrect: ok, score: ok ? 1 : 0, maxScore: 1 };
}

export function renderAnswer({ data }) {
  const { expr, correct } = data;
  return (
    <div className="space-y-2">
      <div>
        <InlineMath math={`Z = ${exprToLatex(expr)}`} />
      </div>
      <Venn3Input
        value={correct}
        disabled
        size={360}
        ghostBackground={false}
        ariaLabel="Poprawnie zamalowany diagram"
      />
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  if (!Array.isArray(userAnswer) || userAnswer.length === 0) return <i>(brak odpowiedzi)</i>;
  return (
    <Venn3Input
      value={userAnswer}
      disabled
      size={360}
      ghostBackground={false}
      ariaLabel="Diagram z Twoją odpowiedzią"
    />
  );
}

export function renderExplanation({ data }) {
  const { expr, correct } = data;
  return (
    <div className="space-y-2">
      <p>
        Używamy reguł: <InlineMath math={"X \\cup Y"} /> - suma,
        <InlineMath math={"\\;X \\cap Y"} /> - część wspólna,
        <InlineMath math={"\\;X \\setminus Y"} /> - różnica.
      </p>
      <p>
        Dla wyrażenia <InlineMath math={exprToLatex(expr)} /> otrzymujemy zaznaczenie poniżej:
      </p>
      <Venn3Input
        value={correct}
        disabled
        size={360}
        ghostBackground={false}
        ariaLabel="Wyjaśnienie - poprawne zaznaczenie"
      />
      <p className="text-sm opacity-80">
        W interaktywnym polu odpowiedzi można było klikać wybrane pola; ponowny klik odznacza.
      </p>
    </div>
  );
}
